import { useEffect, useRef, useState, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';
import {
  VIDEO_SENDER_PROFILES,
  type VideoQualityTier,
  type WebRTCNetworkMetrics,
} from '../utils/videoQuality.ts';
import { isScreenShareTrack, getParticipantScreenShareState } from '../utils/mediaUtils.ts';
import type { RoomTimePolicy } from '../utils/roomTimePolicy.ts';
import {
  readSessionMeetingLinkMeta,
  resolveMeetingLinkCreatedAt,
  writeSessionMeetingLinkMeta,
} from '../utils/meetingLinkExpiry.ts';
import type {
  ChatMessage,
  FloatingReaction,
  ParticipantMediaState,
  ParticipantSignals,
} from '../types/meeting.types.ts';
import type { AgendaTimelineSync } from '../utils/agendaTimeline.ts';
import {
  decryptChatText,
  deriveMeetingAesKey,
  derivePairwiseAesKey,
  encryptChatText,
  encryptPrivateChatText,
  encryptRoomE2eText,
  generateE2eRoomKey,
  generateEcdhKeyPair,
  getChatCipherVersion,
  isEncryptedChatText,
  isPreferredRoomKey,
  unwrapE2eRoomKey,
  wrapE2eRoomKey,
  type ChatCipherContext,
  type E2eRoomKey,
  type EcdhKeyPair,
} from '../utils/meetingEncryption.ts';
import {
  attachPeerMediaEncryption,
  attachReceiverMediaDecryption,
  supportsMediaFrameEncryption,
  withInsertableStreamsConfig,
  type MediaEncryptionGate,
  type MediaKeyHolder,
} from '../utils/mediaFrameEncryption.ts';

const EMOJI_DISPLAY_MS = 10_000;
const FLOATING_REACTION_MS = 3_000;

export interface WebRTCRoomTimeOptions {
  policy: RoomTimePolicy | null;
  onRemotePolicy?: (policy: RoomTimePolicy) => void;
}

export type LobbyStatus = 'idle' | 'waiting' | 'approved' | 'rejected';

export interface WebRTCMeetingOptions extends WebRTCRoomTimeOptions {
  claimHost?: boolean;
  /** 'lobby': odaya girmeden önce host onayı istenir; 'direct': doğrudan katıl */
  joinMode?: 'direct' | 'lobby';
  /** Host tarafı: bekleme odası aktifse gelen istekler onaya düşer */
  requireJoinApproval?: boolean;
  /** Toplantı sahibi: bekleme odası isteklerini yönetebilir (claimHost olmasa bile) */
  canManageLobby?: boolean;
  /** Host tarafı: onay bekleyen katılımcı bildirimi */
  onJoinRequest?: (socketId: string, userName: string) => void;
  onForceMute?: () => void;
  onKicked?: () => void;
  onMeetingEnded?: () => void;
  /** Uzaktan gündem zaman çizelgesi (host atlatma / senkron) */
  onRemoteAgendaTimeline?: (sync: AgendaTimelineSync) => void;
  /** Tutanak politikası (konuşmalar yazıya dökülüyor uyarısı) */
  onRemoteMinutesPolicy?: (enabled: boolean) => void;
}

/** Host yanıt vermezse bu süre sonunda doğrudan katıl (bekleme odası kapalı varsayılır) */
const LOBBY_RESPONSE_TIMEOUT_MS = 6_000;
/** Bekleme odası açıkken misafir en fazla bu kadar bekler */
const LOBBY_STRICT_TIMEOUT_MS = 10 * 60_000;

const SOCKET_URL = process.env.REACT_APP_API_URL || 'https://tbkmeet.com.tr';
const DEFAULT_STUN_URLS = [
  'stun:stun.l.google.com:19302',
  'stun:stun1.l.google.com:19302',
];

function parseIceUrlList(value?: string): string[] {
  if (!value) return [];
  return value
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);
}

function buildIceConfiguration(): RTCConfiguration {
  const stunUrls = parseIceUrlList(process.env.REACT_APP_STUN_URLS) || [];
  const turnUrls = parseIceUrlList(process.env.REACT_APP_TURN_URLS);
  const turnUsername = process.env.REACT_APP_TURN_USERNAME || '';
  const turnCredential = process.env.REACT_APP_TURN_CREDENTIAL || '';

  const iceServers: RTCIceServer[] = [];
  const finalStunUrls = stunUrls.length > 0 ? stunUrls : DEFAULT_STUN_URLS;

  if (finalStunUrls.length > 0) {
    iceServers.push({
      urls: finalStunUrls.length === 1 ? finalStunUrls[0] : finalStunUrls,
    });
  }

  if (turnUrls.length > 0 && turnUsername && turnCredential) {
    iceServers.push({
      urls: turnUrls.length === 1 ? turnUrls[0] : turnUrls,
      username: turnUsername,
      credential: turnCredential,
    });
  } else if (turnUrls.length > 0) {
    console.warn('[WebRTC] TURN URL tanimli ama username/credential eksik. TURN devre disi kaldi.');
  } else {
    console.warn('[WebRTC] TURN sunucusu tanimli degil. Sadece STUN ile devam ediliyor.');
  }

  return {
    iceServers,
    iceCandidatePoolSize: 10,
  };
}

const ICE_CONFIGURATION = buildIceConfiguration();

interface RemoteParticipant {
  socketId: string;
  userName: string;
  stream?: MediaStream;
  isScreenSharing?: boolean;
}

interface PendingParticipant {
  socketId: string;
  name: string;
  createOffer: boolean;
  pendingOffer?: RTCSessionDescriptionInit;
}

export const useWebRTC = (
  meetingId: string,
  userName: string,
  localStream: MediaStream | null,
  videoQuality: VideoQualityTier,
  meetingOptions?: WebRTCMeetingOptions
) => {
  const [participants, setParticipants] = useState<RemoteParticipant[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [mySocketId, setMySocketId] = useState('');
  const [hostSocketId, setHostSocketId] = useState('');
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [participantMediaState, setParticipantMediaState] = useState<
    Record<string, ParticipantMediaState>
  >({});
  const [participantSignals, setParticipantSignals] = useState<
    Record<string, ParticipantSignals>
  >({});
  const [floatingReactions, setFloatingReactions] = useState<FloatingReaction[]>([]);
  const emojiClearTimeoutsRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  
  const socketRef = useRef<Socket | null>(null);
  const mySocketIdRef = useRef<string>('');
  const peerConnectionsRef = useRef<Map<string, RTCPeerConnection>>(new Map());
  const pendingParticipantsRef = useRef<PendingParticipant[]>([]);
  const localStreamRef = useRef<MediaStream | null>(null);
  const isProcessingRef = useRef<Set<string>>(new Set());
  const videoQualityRef = useRef<VideoQualityTier>(videoQuality);
  const statsBytesRef = useRef<Map<string, number>>(new Map());
  const statsTimestampRef = useRef<Map<string, number>>(new Map());
  // Katılımcı isimlerini sakla
  const participantNamesRef = useRef<Map<string, string>>(new Map());
  const meetingOptionsRef = useRef(meetingOptions);
  meetingOptionsRef.current = meetingOptions;
  const hostSocketIdRef = useRef('');
  const encryptionKeyRef = useRef<CryptoKey | null>(null);
  // ECDH: kendi geçici anahtar çiftimiz (özel anahtar tarayıcıdan çıkmaz)
  const ecdhKeyPairRef = useRef<EcdhKeyPair | null>(null);
  // Katılımcıların açık anahtarları (base64) ve türetilmiş ikili anahtar önbelleği
  const peerPublicKeysRef = useRef<Map<string, string>>(new Map());
  const pairwiseKeysRef = useRef<Map<string, Promise<CryptoKey>>>(new Map());
  // Tam E2E: rastgele oda anahtarı (aktif + geçiş dönemindeki eski adaylar)
  const e2eRoomKeyRef = useRef<E2eRoomKey | null>(null);
  const e2eRoomKeyHistoryRef = useRef<CryptoKey[]>([]);
  // Medya (ses/görüntü/ekran) AES-256 çerçeve şifrelemesi — aynı E2E oda anahtarını kullanır
  const mediaKeyHolderRef = useRef<MediaKeyHolder>({ rawB64: null, historyB64: [] });
  // Peer başına gönderim kapısı: karşı taraf media-key-ack gönderene kadar şifresiz akar
  const mediaGatesRef = useRef<Map<string, MediaEncryptionGate>>(new Map());
  // Peer'ların ack'lediği anahtar kimliği (creatorId:createdAt)
  const mediaAcksRef = useRef<Map<string, string>>(new Map());
  const [chatEncryptionEnabled, setChatEncryptionEnabled] = useState(false);
  const [mediaEncryptionEnabled, setMediaEncryptionEnabled] = useState(false);
  const [e2eRoomActive, setE2eRoomActive] = useState(false);
  const [e2eReadyPeers, setE2eReadyPeers] = useState<string[]>([]);
  // Bekleme odası (lobby) durumu
  const [lobbyStatus, setLobbyStatusState] = useState<LobbyStatus>('idle');
  const lobbyStatusRef = useRef<LobbyStatus>('idle');
  const setLobbyStatus = useCallback((status: LobbyStatus) => {
    lobbyStatusRef.current = status;
    setLobbyStatusState(status);
  }, []);
  const lobbyApprovedRef = useRef(false);
  const lobbyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lobbyStrictRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    if (!meetingId) {
      encryptionKeyRef.current = null;
      ecdhKeyPairRef.current = null;
      e2eRoomKeyRef.current = null;
      e2eRoomKeyHistoryRef.current = [];
      mediaKeyHolderRef.current.rawB64 = null;
      mediaKeyHolderRef.current.historyB64 = [];
      mediaGatesRef.current.forEach((gate) => {
        gate.enabled = false;
      });
      mediaGatesRef.current.clear();
      mediaAcksRef.current.clear();
      setChatEncryptionEnabled(false);
      setMediaEncryptionEnabled(false);
      setE2eRoomActive(false);
      return;
    }

    const creatorId =
      typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `${Date.now()}_${Math.random().toString(36).slice(2)}`;

    void Promise.all([
      deriveMeetingAesKey(meetingId),
      generateEcdhKeyPair(),
      generateE2eRoomKey(creatorId),
    ])
      .then(([roomKey, ecdhPair, e2eKey]) => {
        if (cancelled) return;
        encryptionKeyRef.current = roomKey;
        ecdhKeyPairRef.current = ecdhPair;
        // Kendi adayımız; odada daha eski bir anahtar varsa epoch çözümlemesiyle onu benimseriz
        adoptRoomKeyRef.current(e2eKey);
        setChatEncryptionEnabled(true);
        // Anahtar hazırsa ve socket bağlıysa hemen duyur
        broadcastChatPublicKeyRef.current();
      })
      .catch((error) => {
        console.warn('[TBKMeet] AES-256 sohbet anahtarı oluşturulamadı:', error);
        encryptionKeyRef.current = null;
        ecdhKeyPairRef.current = null;
        setChatEncryptionEnabled(false);
      });

    return () => {
      cancelled = true;
    };
  }, [meetingId]);

  /** Kendi ECDH açık anahtarımızı odaya duyurur (sunucu yalnızca açık anahtarı görür). */
  const broadcastChatPublicKey = useCallback(() => {
    const socket = socketRef.current;
    const socketId = mySocketIdRef.current;
    const pair = ecdhKeyPairRef.current;
    if (!socket?.connected || !meetingId || !socketId || !pair) return;
    socket.emit('chat-pubkey', {
      meetingId,
      socketId,
      publicKey: pair.publicKeyB64,
    });
  }, [meetingId]);
  const broadcastChatPublicKeyRef = useRef(broadcastChatPublicKey);
  broadcastChatPublicKeyRef.current = broadcastChatPublicKey;

  /** Aktif medya anahtarının kimliği (ack eşleştirmesi için). */
  const currentMediaKeyId = useCallback((): string | null => {
    const key = e2eRoomKeyRef.current;
    if (!key) return null;
    return `${key.creatorId}:${key.createdAt}`;
  }, []);

  /** Peer için gönderim kapısını getirir/oluşturur (referans sabit kalır). */
  const getMediaGate = useCallback((peerSocketId: string): MediaEncryptionGate => {
    let gate = mediaGatesRef.current.get(peerSocketId);
    if (!gate) {
      gate = { enabled: false };
      mediaGatesRef.current.set(peerSocketId, gate);
    }
    return gate;
  }, []);

  /** "Bu anahtarı aldım, şifreli gönderebilirsin" duyurusu. */
  const broadcastMediaKeyAck = useCallback(() => {
    const socket = socketRef.current;
    const socketId = mySocketIdRef.current;
    const keyId = currentMediaKeyId();
    if (!socket?.connected || !meetingId || !socketId || !keyId) return;
    if (!supportsMediaFrameEncryption()) return;
    socket.emit('media-key-ack', { meetingId, socketId, keyId });
  }, [meetingId, currentMediaKeyId]);
  const broadcastMediaKeyAckRef = useRef(broadcastMediaKeyAck);
  broadcastMediaKeyAckRef.current = broadcastMediaKeyAck;

  /**
   * E2E oda anahtarı adayını benimser. Epoch kuralı: en eski (createdAt, creatorId)
   * anahtar kazanır; böylece tüm istemciler aynı anahtarda buluşur.
   * Önceki anahtarlar geçiş dönemindeki mesajlar için tarihçede tutulur.
   */
  const adoptRoomKey = useCallback((candidate: E2eRoomKey) => {
    const current = e2eRoomKeyRef.current;
    if (!isPreferredRoomKey(candidate, current)) {
      // Mevcut anahtar kazandı; adayı yine de tarihçeye ekle (mesaj/medya çözmek için)
      if (!e2eRoomKeyHistoryRef.current.includes(candidate.key)) {
        e2eRoomKeyHistoryRef.current.push(candidate.key);
      }
      const history = (mediaKeyHolderRef.current.historyB64 ??= []);
      if (!history.includes(candidate.rawB64)) history.push(candidate.rawB64);
      return;
    }
    if (current && !e2eRoomKeyHistoryRef.current.includes(current.key)) {
      e2eRoomKeyHistoryRef.current.push(current.key);
    }
    if (current) {
      const history = (mediaKeyHolderRef.current.historyB64 ??= []);
      if (!history.includes(current.rawB64)) history.push(current.rawB64);
    }
    e2eRoomKeyRef.current = candidate;
    mediaKeyHolderRef.current.rawB64 = candidate.rawB64;
    setE2eRoomActive(true);

    if (supportsMediaFrameEncryption()) {
      const keyId = `${candidate.creatorId}:${candidate.createdAt}`;
      // Anahtar değişti: yalnızca yeni anahtarı ack'lemiş peer'lara şifreli gönder
      mediaGatesRef.current.forEach((gate, peerSocketId) => {
        gate.enabled = mediaAcksRef.current.get(peerSocketId) === keyId;
      });
      setMediaEncryptionEnabled(true);
      console.log('[TBKMeet] Medya AES-256-GCM hazır (ack sonrası peer bazlı aktif)');
      // Karşı taraflara: bu anahtar bende hazır, bana şifreli gönderebilirsiniz
      broadcastMediaKeyAckRef.current();
    } else {
      setMediaEncryptionEnabled(false);
      console.warn(
        '[TBKMeet] Medya AES-256 bu tarayıcıda desteklenmiyor; yalnızca DTLS-SRTP kullanılacak'
      );
    }
  }, []);
  const adoptRoomKeyRef = useRef(adoptRoomKey);
  adoptRoomKeyRef.current = adoptRoomKey;

  /** Belirli bir katılımcıyla ikili AES-256 anahtarını (ECDH → HKDF) döndürür. */
  const getPairwiseKey = useCallback(
    (peerSocketId: string): Promise<CryptoKey> | null => {
      const pair = ecdhKeyPairRef.current;
      const peerPublicKey = peerPublicKeysRef.current.get(peerSocketId);
      if (!pair || !peerPublicKey || !meetingId) return null;

      const cached = pairwiseKeysRef.current.get(peerSocketId);
      if (cached) return cached;

      const derived = derivePairwiseAesKey(pair.privateKey, peerPublicKey, meetingId).catch(
        (error) => {
          pairwiseKeysRef.current.delete(peerSocketId);
          throw error;
        }
      );
      pairwiseKeysRef.current.set(peerSocketId, derived);
      return derived;
    },
    [meetingId]
  );

  /** Aktif E2E oda anahtarını hedef katılımcıya ECDH ile sarıp gönderir. */
  const sendRoomKeyTo = useCallback(
    (peerSocketId: string) => {
      const socket = socketRef.current;
      const roomKey = e2eRoomKeyRef.current;
      const pairwiseKeyPromise = getPairwiseKey(peerSocketId);
      if (!socket?.connected || !meetingId || !roomKey || !pairwiseKeyPromise) return;

      void pairwiseKeyPromise
        .then((pairwiseKey) => wrapE2eRoomKey(roomKey, pairwiseKey))
        .then((wrapped) => {
          if (!socket.connected) return;
          socket.emit('chat-roomkey', {
            meetingId,
            targetSocketId: peerSocketId,
            fromSocketId: mySocketIdRef.current,
            wrapped,
            createdAt: roomKey.createdAt,
            creatorId: roomKey.creatorId,
          });
        })
        .catch((error) => {
          console.warn('[TBKMeet] E2E oda anahtarı gönderilemedi:', error);
        });
    },
    [meetingId, getPairwiseKey]
  );
  const sendRoomKeyToRef = useRef(sendRoomKeyTo);
  sendRoomKeyToRef.current = sendRoomKeyTo;

  const broadcastRoomTimePolicy = useCallback(() => {
    const policy = meetingOptionsRef.current?.policy;
    const socket = socketRef.current;
    if (!policy || !socket?.connected || !meetingId) return;
    socket.emit('room-time-policy', { meetingId, ...policy });
  }, [meetingId]);

  const broadcastHostInfo = useCallback(() => {
    const socket = socketRef.current;
    const hostId = hostSocketIdRef.current;
    if (!socket?.connected || !meetingId || !hostId) return;
    socket.emit('meeting-host-info', {
      meetingId,
      hostSocketId: hostId,
      hostName: userName,
    });
  }, [meetingId, userName]);

  const broadcastMeetingLinkMeta = useCallback(() => {
    const socket = socketRef.current;
    if (!socket?.connected || !meetingId) return;

    void resolveMeetingLinkCreatedAt(meetingId).then((createdAt) => {
      if (!createdAt || !socketRef.current?.connected) return;
      writeSessionMeetingLinkMeta(meetingId, { createdAt });
      socketRef.current.emit('meeting-link-meta', { meetingId, createdAt });
    });
  }, [meetingId]);

  const setParticipantEmoji = useCallback((socketId: string, emoji: string) => {
    setParticipantSignals((prev) => ({
      ...prev,
      [socketId]: { ...prev[socketId], activeEmoji: emoji },
    }));

    const existing = emojiClearTimeoutsRef.current.get(socketId);
    if (existing) clearTimeout(existing);

    const timeout = setTimeout(() => {
      setParticipantSignals((prev) => {
        const current = prev[socketId];
        if (!current) return prev;
        return {
          ...prev,
          [socketId]: { ...current, activeEmoji: undefined },
        };
      });
      emojiClearTimeoutsRef.current.delete(socketId);
    }, EMOJI_DISPLAY_MS);

    emojiClearTimeoutsRef.current.set(socketId, timeout);
  }, []);

  const addFloatingReaction = useCallback((emoji: string) => {
    const id = `float_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
    const leftPercent = 15 + Math.random() * 70;

    setFloatingReactions((prev) => [...prev, { id, emoji, leftPercent }]);
    setTimeout(() => {
      setFloatingReactions((prev) => prev.filter((r) => r.id !== id));
    }, FLOATING_REACTION_MS);
  }, []);

  const applyReaction = useCallback(
    (socketId: string, emoji: string) => {
      setParticipantEmoji(socketId, emoji);
      addFloatingReaction(emoji);
    },
    [addFloatingReaction, setParticipantEmoji]
  );
  const applyReactionRef = useRef(applyReaction);
  applyReactionRef.current = applyReaction;

  const claimHostIfNeeded = useCallback(() => {
    if (!meetingOptionsRef.current?.claimHost || hostSocketIdRef.current) return;
    const socketId = mySocketIdRef.current;
    if (!socketId) return;
    hostSocketIdRef.current = socketId;
    setHostSocketId(socketId);
    broadcastHostInfo();
  }, [broadcastHostInfo]);

  useEffect(() => {
    localStreamRef.current = localStream;
  }, [localStream]);

  useEffect(() => {
    if (isConnected && meetingOptionsRef.current?.claimHost) {
      claimHostIfNeeded();
    }
  }, [isConnected, meetingOptions?.claimHost, claimHostIfNeeded]);

  const applyVideoSenderParameters = useCallback(async (pc: RTCPeerConnection) => {
    const profile = VIDEO_SENDER_PROFILES[videoQualityRef.current];
    const videoSenders = pc.getSenders().filter((sender) => sender.track?.kind === 'video');

    await Promise.all(videoSenders.map(async (sender) => {
      try {
        const params = sender.getParameters();
        if (!params.encodings || params.encodings.length === 0) {
          params.encodings = [{}];
        }

        params.degradationPreference = 'balanced';
        params.encodings[0].maxBitrate = profile.maxBitrate;
        params.encodings[0].maxFramerate = profile.maxFramerate;
        params.encodings[0].scaleResolutionDownBy = profile.scaleResolutionDownBy;

        await sender.setParameters(params);
      } catch (error) {
        console.warn('Video sender parametresi uygulanamadi:', error);
      }
    }));
  }, []);

  useEffect(() => {
    videoQualityRef.current = videoQuality;
    peerConnectionsRef.current.forEach((pc) => {
      void applyVideoSenderParameters(pc);
    });
  }, [applyVideoSenderParameters, videoQuality]);

  const createPeerConnection = useCallback((socketId: string, participantName: string, stream: MediaStream): RTCPeerConnection => {
    console.log('=== Creating peer connection ===');
    console.log('For:', socketId, 'Name:', participantName);

    // İsmi kaydet
    if (participantName && participantName !== 'Katılımcı') {
      participantNamesRef.current.set(socketId, participantName);
    }
    
    // Kayıtlı ismi al
    const savedName = participantNamesRef.current.get(socketId) || participantName;

    if (peerConnectionsRef.current.has(socketId)) {
      console.log('Peer connection already exists for:', socketId);
      return peerConnectionsRef.current.get(socketId)!;
    }

    const pc = new RTCPeerConnection(withInsertableStreamsConfig(ICE_CONFIGURATION));
    peerConnectionsRef.current.set(socketId, pc);

    stream.getTracks().forEach((track) => {
      console.log('Adding local track:', track.kind);
      pc.addTrack(track, stream);
    });

    const encryptedSenders = attachPeerMediaEncryption(
      pc,
      mediaKeyHolderRef.current,
      getMediaGate(socketId)
    );
    if (encryptedSenders > 0) {
      console.log(
        '[TBKMeet] Gönderim AES-256 bağlandı (ack bekleniyor):',
        socketId,
        `(${encryptedSenders} track)`
      );
    } else if (supportsMediaFrameEncryption()) {
      console.warn('[TBKMeet] Insertable Streams var ama sender şifreleme bağlanamadı:', socketId);
    }
    void applyVideoSenderParameters(pc);

    pc.ontrack = (event) => {
      console.log('>>> REMOTE TRACK RECEIVED:', event.track.kind, 'from:', socketId);
      const decryptOk = attachReceiverMediaDecryption(
        event.receiver,
        mediaKeyHolderRef.current
      );
      if (decryptOk) {
        console.log('[TBKMeet] Alım AES-256 çözücü bağlandı:', event.track.kind, socketId);
      }
      const remoteStream = event.streams[0];
      
      if (remoteStream) {
        const finalName = participantNamesRef.current.get(socketId) || savedName;
        const sharingScreen = event.track.kind === 'video'
          && getParticipantScreenShareState(remoteStream, isScreenShareTrack(event.track));

        const updateParticipant = (prev: RemoteParticipant[]) => {
          const exists = prev.find(p => p.socketId === socketId);
          if (exists) {
            return prev.map(p =>
              p.socketId === socketId
                ? { ...p, stream: remoteStream, userName: finalName, isScreenSharing: sharingScreen }
                : p
            );
          }
          return [...prev, {
            socketId,
            userName: finalName,
            stream: remoteStream,
            isScreenSharing: sharingScreen,
          }];
        };

        setParticipants(updateParticipant);

        if (event.track.kind === 'video') {
          const refreshShareState = () => {
            const sharing = getParticipantScreenShareState(remoteStream, isScreenShareTrack(event.track));
            setParticipants(prev => prev.map(p =>
              p.socketId === socketId ? { ...p, isScreenSharing: sharing } : p
            ));
          };

          event.track.onended = () => {
            setParticipants(prev => prev.map(p =>
              p.socketId === socketId ? { ...p, isScreenSharing: false } : p
            ));
          };

          event.track.onmute = refreshShareState;
          event.track.onunmute = refreshShareState;
        }
      }
    };

    pc.onicecandidate = (event) => {
      if (event.candidate && socketRef.current) {
        socketRef.current.emit('ice-candidate', { to: socketId, candidate: event.candidate });
      }
    };

    pc.oniceconnectionstatechange = () => {
      console.log('ICE state for', socketId, ':', pc.iceConnectionState);
      
      if (pc.iceConnectionState === 'connected' || pc.iceConnectionState === 'completed') {
        console.log('✅ Peer connection established with:', socketId);
      }
      
      if (pc.iceConnectionState === 'failed') {
        console.log('❌ Peer connection failed:', socketId);
      }
      
      // Disconnected durumunda hemen silme, reconnect olabilir
      if (pc.iceConnectionState === 'disconnected') {
        console.log('⚠️ Peer connection disconnected:', socketId);
      }
    };

    pc.onconnectionstatechange = () => {
      console.log('Connection state for', socketId, ':', pc.connectionState);
    };

    setParticipants(prev => {
      if (prev.find(p => p.socketId === socketId)) return prev;
      return [...prev, { socketId, userName: savedName }];
    });

    return pc;
  }, [applyVideoSenderParameters, getMediaGate]);

  const createAndSendOffer = useCallback(async (socketId: string, participantName: string, stream: MediaStream) => {
    if (isProcessingRef.current.has(socketId)) {
      console.log('Already processing offer for:', socketId);
      return;
    }
    
    if (mySocketIdRef.current > socketId) {
      console.log('Skipping offer - waiting for other peer to initiate (glare prevention)');
      return;
    }
    
    isProcessingRef.current.add(socketId);
    
    // İsmi kaydet
    if (participantName && participantName !== 'Katılımcı') {
      participantNamesRef.current.set(socketId, participantName);
    }
    
    try {
      const pc = createPeerConnection(socketId, participantName, stream);
      
      if (pc.signalingState !== 'stable') {
        console.log('Skipping offer - signaling state:', pc.signalingState);
        return;
      }
      
      console.log('Creating offer for:', socketId);
      const offer = await pc.createOffer({
        offerToReceiveAudio: true,
        offerToReceiveVideo: true
      });
      
      await pc.setLocalDescription(offer);
      
      console.log('Sending offer to:', socketId);
      socketRef.current?.emit('offer', { to: socketId, offer: pc.localDescription });
    } catch (error) {
      console.error('Error creating offer:', error);
    } finally {
      isProcessingRef.current.delete(socketId);
    }
  }, [createPeerConnection]);

  const handleOffer = useCallback(async (fromSocketId: string, offer: RTCSessionDescriptionInit, participantName: string, stream: MediaStream) => {
    console.log('Handling offer from:', fromSocketId, 'Name:', participantName);
    
    if (isProcessingRef.current.has(fromSocketId)) {
      console.log('Already processing offer from:', fromSocketId);
      return;
    }
    
    isProcessingRef.current.add(fromSocketId);
    
    // İsmi kaydet
    const savedName = participantNamesRef.current.get(fromSocketId) || participantName;
    
    try {
      let pc = peerConnectionsRef.current.get(fromSocketId);
      
      if (pc && pc.signalingState === 'have-local-offer') {
        if (mySocketIdRef.current > fromSocketId) {
          console.log('Glare detected - rolling back my offer');
          await pc.setLocalDescription({ type: 'rollback' });
        } else {
          console.log('Glare detected - ignoring incoming offer');
          return;
        }
      }
      
      if (!pc) {
        pc = createPeerConnection(fromSocketId, savedName, stream);
      }
      
      console.log('Setting remote description (offer)');
      await pc.setRemoteDescription(new RTCSessionDescription(offer));
      
      console.log('Creating answer');
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      
      console.log('Sending answer to:', fromSocketId);
      socketRef.current?.emit('answer', { to: fromSocketId, answer: pc.localDescription });
    } catch (error) {
      console.error('Error handling offer:', error);
    } finally {
      isProcessingRef.current.delete(fromSocketId);
    }
  }, [createPeerConnection]);

  useEffect(() => {
    if (!meetingId) return;

    const emojiTimeouts = emojiClearTimeoutsRef.current;
    const token = localStorage.getItem('tbkmeet_token');
    
    console.log('Connecting to socket...', SOCKET_URL);
    console.log('Token exists:', !!token);
    
    const socket = io(SOCKET_URL, {
      path: '/meeting-socket/',
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000
    });
    
    socketRef.current = socket;

    const proceedJoin = () => {
      console.log('Joining room:', meetingId, 'as:', userName);
      lobbyApprovedRef.current = true;
      setLobbyStatus('approved');
      const policy = meetingOptionsRef.current?.policy;
      socket.emit('join-room', {
        meetingId,
        userName,
        roomTimePolicy: policy ?? undefined,
      });
      if (policy) {
        socket.emit('room-time-policy', { meetingId, ...policy });
      }
      // Bekleme odası açıksa odadaki misafirlere duyur (otomatik girişi engellemek için)
      if (meetingOptionsRef.current?.requireJoinApproval) {
        socket.emit('waiting-room-policy', { meetingId, enabled: true });
      }
      claimHostIfNeeded();
      broadcastMeetingLinkMeta();
      broadcastChatPublicKeyRef.current();
      // Anahtar socket bağlanmadan önce hazır olduysa ack'i şimdi duyur
      broadcastMediaKeyAckRef.current();
    };

    const scheduleLobbyFallback = () => {
      if (lobbyTimeoutRef.current) clearTimeout(lobbyTimeoutRef.current);
      const delay = lobbyStrictRef.current ? LOBBY_STRICT_TIMEOUT_MS : LOBBY_RESPONSE_TIMEOUT_MS;
      lobbyTimeoutRef.current = setTimeout(() => {
        if (!lobbyApprovedRef.current && lobbyStatusRef.current === 'waiting') {
          if (lobbyStrictRef.current) {
            console.warn('[TBKMeet] Bekleme odası: host yanıt vermedi (10 dk)');
            setLobbyStatus('rejected');
          } else {
            console.log('Lobby yanıtı gelmedi, doğrudan katılınıyor');
            proceedJoin();
          }
        }
      }, delay);
    };

    socket.on('connect', () => {
      console.log('✅ Socket connected:', socket.id);
      mySocketIdRef.current = socket.id || '';
      setMySocketId(socket.id || '');
      setIsConnected(true);

      const useLobby =
        meetingOptionsRef.current?.joinMode === 'lobby' && !lobbyApprovedRef.current;

      if (!useLobby) {
        proceedJoin();
        return;
      }

      // Bekleme odası: önce katılım isteği gönder, host onayını bekle
      setLobbyStatus('waiting');
      const emitJoinRequest = () => {
        if (lobbyApprovedRef.current || lobbyStatusRef.current !== 'waiting') return;
        socket.emit('join-request', {
          meetingId,
          socketId: socket.id,
          userName,
        });
      };
      emitJoinRequest();
      // İstek kaybolabilir (host henüz bağlanmamış olabilir) — aralıklarla yinele
      const retry1 = setTimeout(emitJoinRequest, 2_000);
      const retry2 = setTimeout(emitJoinRequest, 4_000);
      const retry3 = setTimeout(emitJoinRequest, 8_000);

      scheduleLobbyFallback();

      // Cleanup retry timers when approved/rejected
      const clearRetries = () => {
        clearTimeout(retry1);
        clearTimeout(retry2);
        clearTimeout(retry3);
      };
      socket.once('join-response', clearRetries);
    });

    // Bekleme odası politikası: host açtıysa misafir otomatik girmesin
    socket.on(
      'waiting-room-policy',
      (data: { meetingId?: string; enabled?: boolean }) => {
        if (data.meetingId && data.meetingId !== meetingId) return;
        if (!data.enabled) return;
        if (lobbyApprovedRef.current || lobbyStatusRef.current !== 'waiting') return;

        lobbyStrictRef.current = true;
        console.log('[TBKMeet] Bekleme odası aktif — host onayı bekleniyor');
        scheduleLobbyFallback();
      }
    );

    // Host tarafı: katılım isteği geldi
    socket.on(
      'join-request',
      (data: { meetingId?: string; socketId?: string; userName?: string }) => {
        if (data.meetingId && data.meetingId !== meetingId) return;
        if (!data.socketId || data.socketId === mySocketIdRef.current) return;

        const opts = meetingOptionsRef.current;
        const amHost = hostSocketIdRef.current === mySocketIdRef.current;
        const amHostCandidate = !hostSocketIdRef.current && !!opts?.claimHost;
        const canManageLobby = !!opts?.canManageLobby;

        // Bekleme odası kapalıysa otomatik onayla (yalnızca host/yönetici yanıtlar)
        if (!opts?.requireJoinApproval) {
          if (!amHost && !amHostCandidate && !canManageLobby) return;
          socket.emit('join-response', {
            meetingId,
            targetSocketId: data.socketId,
            approved: true,
          });
          return;
        }

        // Bekleme odası açık — yalnızca toplantı sahibi / host popup gösterir
        if (!canManageLobby && !amHost && !amHostCandidate) return;

        // Host kimliği henüz atanmadıysa sahip olarak üstlen
        if (!hostSocketIdRef.current && (canManageLobby || amHostCandidate)) {
          hostSocketIdRef.current = mySocketIdRef.current;
          setHostSocketId(mySocketIdRef.current);
          broadcastHostInfo();
        }

        console.log('[TBKMeet] Katılım isteği:', data.userName, data.socketId);
        opts?.onJoinRequest?.(data.socketId, data.userName || 'Katılımcı');
      }
    );

    // Katılımcı tarafı: host'un onay/red yanıtı
    socket.on(
      'join-response',
      (data: { meetingId?: string; targetSocketId?: string; approved?: boolean }) => {
        if (data.meetingId && data.meetingId !== meetingId) return;
        if (data.targetSocketId !== mySocketIdRef.current) return;
        if (lobbyApprovedRef.current || lobbyStatusRef.current !== 'waiting') return;

        if (lobbyTimeoutRef.current) {
          clearTimeout(lobbyTimeoutRef.current);
          lobbyTimeoutRef.current = null;
        }

        if (data.approved) {
          proceedJoin();
        } else {
          setLobbyStatus('rejected');
        }
      }
    );

    socket.on(
      'chat-pubkey',
      (data: { meetingId?: string; socketId?: string; publicKey?: string }) => {
        if (data.meetingId && data.meetingId !== meetingId) return;
        if (!data.socketId || !data.publicKey) return;
        if (data.socketId === mySocketIdRef.current) return;

        const known = peerPublicKeysRef.current.get(data.socketId);
        if (known === data.publicKey) return;

        peerPublicKeysRef.current.set(data.socketId, data.publicKey);
        pairwiseKeysRef.current.delete(data.socketId);
        setE2eReadyPeers(Array.from(peerPublicKeysRef.current.keys()));

        // Karşı taraf bizim anahtarımızı görmemiş olabilir (geç katılım) — yanıtla
        broadcastChatPublicKeyRef.current();

        // Aktif E2E oda anahtarını yeni katılımcıya sarıp ilet
        sendRoomKeyToRef.current(data.socketId);

        // Geç katılan peer bizim ack'imizi görmemiş olabilir — tekrar duyur
        broadcastMediaKeyAckRef.current();
      }
    );

    socket.on(
      'media-key-ack',
      (data: { meetingId?: string; socketId?: string; keyId?: string }) => {
        if (data.meetingId && data.meetingId !== meetingId) return;
        if (!data.socketId || !data.keyId) return;
        if (data.socketId === mySocketIdRef.current) return;

        mediaAcksRef.current.set(data.socketId, data.keyId);
        const active = e2eRoomKeyRef.current;
        const activeKeyId = active ? `${active.creatorId}:${active.createdAt}` : null;
        const gate = mediaGatesRef.current.get(data.socketId);
        if (gate && activeKeyId && data.keyId === activeKeyId) {
          if (!gate.enabled) {
            gate.enabled = true;
            console.log('[TBKMeet] Medya AES-256 gönderim aktif →', data.socketId);
          }
        }
      }
    );

    socket.on(
      'chat-roomkey',
      (data: {
        meetingId?: string;
        targetSocketId?: string;
        fromSocketId?: string;
        wrapped?: string;
        createdAt?: number;
        creatorId?: string;
      }) => {
        if (data.meetingId && data.meetingId !== meetingId) return;
        if (data.targetSocketId !== mySocketIdRef.current) return;
        if (!data.fromSocketId || !data.wrapped) return;
        if (typeof data.createdAt !== 'number' || !data.creatorId) return;

        const current = e2eRoomKeyRef.current;
        if (current && current.creatorId === data.creatorId && current.createdAt === data.createdAt) {
          return; // Zaten aynı anahtar
        }

        const pairwiseKeyPromise = getPairwiseKey(data.fromSocketId);
        if (!pairwiseKeyPromise) return;

        void pairwiseKeyPromise
          .then((pairwiseKey) =>
            unwrapE2eRoomKey(data.wrapped!, pairwiseKey, data.createdAt!, data.creatorId!)
          )
          .then((candidate) => {
            adoptRoomKeyRef.current(candidate);
          })
          .catch((error) => {
            console.warn('[TBKMeet] E2E oda anahtarı açılamadı:', error);
          });
      }
    );

    socket.on('room-time-policy', (data: RoomTimePolicy & { meetingId?: string }) => {
      if (data.meetingId && data.meetingId !== meetingId) return;
      if (typeof data.startedAt !== 'number' || typeof data.limitSeconds !== 'number') return;
      meetingOptionsRef.current?.onRemotePolicy?.({
        startedAt: data.startedAt,
        limitSeconds: data.limitSeconds,
      });
    });

    socket.on(
      'agenda-timeline',
      (data: AgendaTimelineSync & { meetingId?: string }) => {
        if (data.meetingId && data.meetingId !== meetingId) return;
        if (typeof data.startedAt !== 'number') return;
        if (typeof data.currentIndex !== 'number') return;
        meetingOptionsRef.current?.onRemoteAgendaTimeline?.({
          startedAt: data.startedAt,
          currentIndex: data.currentIndex,
          advancedAt:
            typeof data.advancedAt === 'number' ? data.advancedAt : null,
        });
      }
    );

    socket.on(
      'minutes-policy',
      (data: { meetingId?: string; enabled?: boolean }) => {
        if (data.meetingId && data.meetingId !== meetingId) return;
        if (typeof data.enabled !== 'boolean') return;
        meetingOptionsRef.current?.onRemoteMinutesPolicy?.(data.enabled);
      }
    );

    socket.on('meeting-link-meta', (data: { meetingId?: string; createdAt?: number }) => {
      if (data.meetingId && data.meetingId !== meetingId) return;
      if (typeof data.createdAt !== 'number') return;
      const existing = readSessionMeetingLinkMeta(meetingId);
      if (!existing || data.createdAt <= existing.createdAt) {
        writeSessionMeetingLinkMeta(meetingId, { createdAt: data.createdAt });
      }
    });

    socket.on(
      'meeting-host-info',
      (data: { meetingId?: string; hostSocketId?: string; hostName?: string }) => {
        if (data.meetingId && data.meetingId !== meetingId) return;
        if (!data.hostSocketId) return;
        if (!hostSocketIdRef.current) {
          hostSocketIdRef.current = data.hostSocketId;
          setHostSocketId(data.hostSocketId);
        }
      }
    );

    socket.on('chat-message', (data: ChatMessage & { meetingId?: string }) => {
      if (data.meetingId && data.meetingId !== meetingId) return;

      void (async () => {
        let displayText = data.text;

        if (isEncryptedChatText(data.text)) {
          const version = getChatCipherVersion(data.text);
          const ctx: ChatCipherContext = {
            meetingId,
            messageId: data.id,
            senderId: data.senderId,
          };

          const decryptV4 = async (): Promise<string> => {
            // Aktif anahtar + geçiş dönemindeki eski anahtarları sırayla dene
            const candidates: CryptoKey[] = [];
            if (e2eRoomKeyRef.current) candidates.push(e2eRoomKeyRef.current.key);
            candidates.push(...e2eRoomKeyHistoryRef.current);
            for (const candidate of candidates) {
              try {
                return await decryptChatText(data.text, candidate, ctx);
              } catch {
                // sıradaki anahtarı dene
              }
            }
            throw new Error('E2E oda anahtarı eşleşmedi');
          };

          try {
            if (version === 3) {
              // Kişiye özel E2E: sadece gerçek alıcı çözebilir
              if (data.toSocketId !== mySocketIdRef.current) {
                // Bize ait olmayan özel mesaj — çözülemez, listeye de girmez
                return;
              }
              const pairwiseKeyPromise = getPairwiseKey(data.senderId);
              if (!pairwiseKeyPromise) {
                throw new Error('Gönderenin açık anahtarı yok');
              }
              displayText = await decryptChatText(data.text, await pairwiseKeyPromise, ctx);
            } else if (version === 4) {
              try {
                displayText = await decryptV4();
              } catch {
                // Anahtar dağıtımı henüz tamamlanmamış olabilir — kısa bekleyip tekrar dene
                await new Promise((resolve) => setTimeout(resolve, 1500));
                displayText = await decryptV4();
              }
            } else {
              const key = encryptionKeyRef.current;
              if (!key) throw new Error('Oda anahtarı hazır değil');
              displayText = await decryptChatText(data.text, key, ctx);
            }
          } catch {
            displayText = '[Şifreli mesaj çözülemedi]';
          }
        }

        setChatMessages((prev) => {
          if (prev.some((m) => m.id === data.id)) return prev;
          return [...prev, { ...data, text: displayText }];
        });
      })();
    });

    socket.on(
      'media-state',
      (data: {
        meetingId?: string;
        socketId: string;
        isAudioEnabled: boolean;
        isVideoEnabled: boolean;
        isScreenSharing?: boolean;
      }) => {
        if (data.meetingId && data.meetingId !== meetingId) return;
        if (!data.socketId) return;
        setParticipantMediaState((prev) => ({
          ...prev,
          [data.socketId]: {
            isAudioEnabled: data.isAudioEnabled,
            isVideoEnabled: data.isVideoEnabled,
            isScreenSharing: data.isScreenSharing,
          },
        }));
      }
    );

    socket.on(
      'force-mute',
      (data: { meetingId?: string; targetSocketId?: string }) => {
        if (data.meetingId && data.meetingId !== meetingId) return;
        if (data.targetSocketId !== mySocketIdRef.current) return;
        meetingOptionsRef.current?.onForceMute?.();
      }
    );

    socket.on(
      'kick-participant',
      (data: { meetingId?: string; targetSocketId?: string }) => {
        if (data.meetingId && data.meetingId !== meetingId) return;
        if (data.targetSocketId !== mySocketIdRef.current) return;
        meetingOptionsRef.current?.onKicked?.();
      }
    );

    socket.on(
      'meeting-ended',
      (data: { meetingId?: string; bySocketId?: string }) => {
        if (data.meetingId && data.meetingId !== meetingId) return;
        if (data.bySocketId && data.bySocketId === mySocketIdRef.current) return;
        meetingOptionsRef.current?.onMeetingEnded?.();
      }
    );

    socket.on(
      'meeting-reaction',
      (data: { meetingId?: string; socketId?: string; emoji?: string }) => {
        if (data.meetingId && data.meetingId !== meetingId) return;
        if (!data.socketId || !data.emoji) return;
        applyReactionRef.current(data.socketId, data.emoji);
      }
    );

    socket.on(
      'hand-raise',
      (data: { meetingId?: string; socketId?: string; raised?: boolean }) => {
        if (data.meetingId && data.meetingId !== meetingId) return;
        if (!data.socketId) return;
        setParticipantSignals((prev) => ({
          ...prev,
          [data.socketId!]: { ...prev[data.socketId!], handRaised: !!data.raised },
        }));
      }
    );

    socket.on('connect_error', (error) => {
      console.error('❌ Socket connection error:', error.message);
      setIsConnected(false);
    });

    socket.on('disconnect', (reason) => {
      console.log('Socket disconnected:', reason);
      setIsConnected(false);
    });

    socket.on('existing-participants', (existing: Array<{ socketId: string; userName: string }>) => {
      console.log('📋 Existing participants:', existing.length, existing);
      
      existing.forEach(p => {
        if (p.socketId !== mySocketIdRef.current) {
          // İsmi kaydet
          participantNamesRef.current.set(p.socketId, p.userName);
          
          pendingParticipantsRef.current.push({
            socketId: p.socketId,
            name: p.userName || 'Katılımcı',
            createOffer: true
          });
        }
      });
      
      if (localStreamRef.current && pendingParticipantsRef.current.length > 0) {
        processPendingParticipants(localStreamRef.current);
      }
    });

    socket.on('user-joined', (data: { socketId: string; userName: string }) => {
      console.log('👤 User joined:', data.userName, data.socketId);
      
      if (data.socketId === mySocketIdRef.current) {
        console.log('Ignoring self join');
        return;
      }

      broadcastRoomTimePolicy();
      broadcastHostInfo();
      broadcastChatPublicKeyRef.current();
      
      // İsmi kaydet
      participantNamesRef.current.set(data.socketId, data.userName);
      
      if (localStreamRef.current) {
        createAndSendOffer(data.socketId, data.userName, localStreamRef.current);
      } else {
        console.log('No local stream yet, queuing participant');
        pendingParticipantsRef.current.push({
          socketId: data.socketId,
          name: data.userName,
          createOffer: true
        });
      }
    });

    socket.on('user-left', (data: { socketId: string }) => {
      console.log('👋 User left:', data.socketId);
      
      const pc = peerConnectionsRef.current.get(data.socketId);
      if (pc) {
        pc.close();
        peerConnectionsRef.current.delete(data.socketId);
      }
      
      participantNamesRef.current.delete(data.socketId);
      peerPublicKeysRef.current.delete(data.socketId);
      pairwiseKeysRef.current.delete(data.socketId);
      const leftGate = mediaGatesRef.current.get(data.socketId);
      if (leftGate) leftGate.enabled = false;
      mediaGatesRef.current.delete(data.socketId);
      mediaAcksRef.current.delete(data.socketId);
      setE2eReadyPeers(Array.from(peerPublicKeysRef.current.keys()));
      setParticipants(prev => prev.filter(p => p.socketId !== data.socketId));
      setParticipantMediaState((prev) => {
        const next = { ...prev };
        delete next[data.socketId];
        return next;
      });
    });

    socket.on('offer', async (data: { from: string; offer: RTCSessionDescriptionInit }) => {
      console.log('📨 Received offer from:', data.from);
      
      // Kayıtlı ismi al
      const savedName = participantNamesRef.current.get(data.from) || 'Katılımcı';
      
      if (localStreamRef.current) {
        await handleOffer(data.from, data.offer, savedName, localStreamRef.current);
      } else {
        console.log('No local stream, queuing offer');
        pendingParticipantsRef.current.push({
          socketId: data.from,
          name: savedName,
          createOffer: false,
          pendingOffer: data.offer
        });
      }
    });

    socket.on('answer', async (data: { from: string; answer: RTCSessionDescriptionInit }) => {
      console.log('📨 Received answer from:', data.from);
      
      const pc = peerConnectionsRef.current.get(data.from);
      if (pc) {
        if (pc.signalingState === 'have-local-offer') {
          console.log('Setting remote description (answer)');
          await pc.setRemoteDescription(new RTCSessionDescription(data.answer));
        } else {
          console.log('Ignoring answer - wrong state:', pc.signalingState);
        }
      } else {
        console.log('No peer connection found for:', data.from);
      }
    });

    socket.on('ice-candidate', async (data: { from: string; candidate: RTCIceCandidateInit }) => {
      const pc = peerConnectionsRef.current.get(data.from);
      if (pc && data.candidate) {
        try {
          await pc.addIceCandidate(new RTCIceCandidate(data.candidate));
        } catch (e) {
          console.log('ICE candidate error:', e);
        }
      }
    });

    return () => {
      console.log('Cleaning up WebRTC...');
      if (lobbyTimeoutRef.current) {
        clearTimeout(lobbyTimeoutRef.current);
        lobbyTimeoutRef.current = null;
      }
      lobbyApprovedRef.current = false;
      lobbyStrictRef.current = false;
      lobbyStatusRef.current = 'idle';
      setLobbyStatusState('idle');
      socket.emit('leave-room', meetingId);
      socket.disconnect();

      // WebRTC cleanup'ta ref'in en güncel değeri kasıtlı kullanılıyor
      // (kapanırken o anki tüm peer connection'ları kapatmak için).
      /* eslint-disable react-hooks/exhaustive-deps */
      peerConnectionsRef.current.forEach(pc => pc.close());
      peerConnectionsRef.current.clear();
      pendingParticipantsRef.current = [];
      isProcessingRef.current.clear();
      participantNamesRef.current.clear();
      peerPublicKeysRef.current.clear();
      pairwiseKeysRef.current.clear();
      e2eRoomKeyRef.current = null;
      e2eRoomKeyHistoryRef.current = [];
      mediaKeyHolderRef.current.rawB64 = null;
      mediaKeyHolderRef.current.historyB64 = [];
      mediaGatesRef.current.forEach((gate) => {
        gate.enabled = false;
      });
      mediaGatesRef.current.clear();
      mediaAcksRef.current.clear();
      /* eslint-enable react-hooks/exhaustive-deps */

      setParticipants([]);
      setIsConnected(false);
      setMySocketId('');
      setHostSocketId('');
      hostSocketIdRef.current = '';
      setChatMessages([]);
      setParticipantMediaState({});
      setParticipantSignals({});
      setE2eReadyPeers([]);
      setE2eRoomActive(false);
      setMediaEncryptionEnabled(false);
      setFloatingReactions([]);
      emojiTimeouts.forEach((t) => clearTimeout(t));
      emojiTimeouts.clear();
    };
    // processPendingParticipants stream geldiğinde manuel çağrılıyor;
    // deps'e eklenirse socket sürekli reconnect olur.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meetingId, userName, createAndSendOffer, handleOffer]);

  const processPendingParticipants = useCallback((stream: MediaStream) => {
    console.log('Processing pending participants:', pendingParticipantsRef.current.length);
    
    const pending = [...pendingParticipantsRef.current];
    pendingParticipantsRef.current = [];
    
    pending.forEach(async (p) => {
      if (peerConnectionsRef.current.has(p.socketId)) {
        console.log('Peer connection already exists, skipping:', p.socketId);
        return;
      }
      
      if (p.pendingOffer) {
        await handleOffer(p.socketId, p.pendingOffer, p.name, stream);
      } else if (p.createOffer) {
        await createAndSendOffer(p.socketId, p.name, stream);
      }
    });
  }, [createAndSendOffer, handleOffer]);

  useEffect(() => {
    if (!localStream) return;
    
    if (pendingParticipantsRef.current.length > 0) {
      processPendingParticipants(localStream);
    }
  }, [localStream, processPendingParticipants]);

  const collectNetworkMetrics = useCallback(async (): Promise<WebRTCNetworkMetrics | null> => {
    const entries = Array.from(peerConnectionsRef.current.entries());
    if (entries.length === 0) return null;

    let packetsLost = 0;
    let packetsSent = 0;
    let maxRtt = 0;
    let minFps = Number.POSITIVE_INFINITY;
    let bandwidthLimited = false;
    let outboundBitrateKbps = 0;

    for (const [peerId, pc] of entries) {
      const stats = await pc.getStats();

      for (const report of stats.values()) {
        if (report.type === 'outbound-rtp' && report.kind === 'video') {
          packetsSent += report.packetsSent ?? 0;
          packetsLost += report.packetsLost ?? 0;

          if (typeof report.framesPerSecond === 'number') {
            minFps = Math.min(minFps, report.framesPerSecond);
          }

          if (report.qualityLimitationReason === 'bandwidth') {
            bandwidthLimited = true;
          }

          const key = `${peerId}:${report.id}`;
          const bytes = report.bytesSent ?? 0;
          const timestamp = report.timestamp;
          const prevBytes = statsBytesRef.current.get(key);
          const prevTimestamp = statsTimestampRef.current.get(key);

          if (prevBytes != null && prevTimestamp != null && timestamp > prevTimestamp) {
            outboundBitrateKbps += ((bytes - prevBytes) * 8) / (timestamp - prevTimestamp);
          }

          statsBytesRef.current.set(key, bytes);
          statsTimestampRef.current.set(key, timestamp);
        }

        if (report.type === 'candidate-pair' && report.state === 'succeeded') {
          const rttMs = (report.currentRoundTripTime ?? 0) * 1000;
          if (rttMs > maxRtt) maxRtt = rttMs;
        }

        if (report.type === 'remote-inbound-rtp' && report.kind === 'video') {
          const rttMs = (report.roundTripTime ?? 0) * 1000;
          if (rttMs > maxRtt) maxRtt = rttMs;
        }
      }
    }

    const packetLossPercent = packetsSent > 0 ? (packetsLost / packetsSent) * 100 : 0;

    return {
      peerCount: entries.length,
      packetLossPercent,
      roundTripTimeMs: maxRtt,
      outboundFps: Number.isFinite(minFps) ? minFps : 30,
      outboundBitrateKbps,
      bandwidthLimited,
    };
  }, []);

  const sendChatMessage = useCallback(
    (text: string, scope: 'public' | 'private', toSocketId?: string) => {
      const socket = socketRef.current;
      const senderId = mySocketIdRef.current;
      if (!socket?.connected || !meetingId || !senderId || !text.trim()) return;

      const plainText = text.trim();
      const message: ChatMessage & { meetingId: string } = {
        id: `${senderId}_${Date.now()}`,
        senderId,
        senderName: userName,
        text: plainText,
        timestamp: Date.now(),
        scope,
        toSocketId: scope === 'private' ? toSocketId : undefined,
        meetingId,
      };

      void (async () => {
        let wireText = plainText;
        const ctx: ChatCipherContext = {
          meetingId,
          messageId: message.id,
          senderId,
        };

        try {
          if (scope === 'private' && toSocketId) {
            // Önce kişiye özel E2E anahtar dene (ECDH) — sadece alıcı çözebilir
            const pairwiseKeyPromise = getPairwiseKey(toSocketId);
            if (pairwiseKeyPromise) {
              wireText = await encryptPrivateChatText(plainText, await pairwiseKeyPromise, ctx);
            } else if (encryptionKeyRef.current) {
              // Alıcının açık anahtarı henüz gelmediyse türetilmiş anahtara düş
              wireText = await encryptChatText(plainText, encryptionKeyRef.current, ctx);
            }
          } else if (e2eRoomKeyRef.current) {
            // Tam E2E: rastgele oda anahtarı — sunucu bu anahtarı türetemez
            wireText = await encryptRoomE2eText(plainText, e2eRoomKeyRef.current.key, ctx);
          } else if (encryptionKeyRef.current) {
            wireText = await encryptChatText(plainText, encryptionKeyRef.current, ctx);
          }
        } catch (error) {
          console.warn('[TBKMeet] Sohbet şifreleme hatası:', error);
        }

        socket.emit('chat-message', { ...message, text: wireText });
        setChatMessages((prev) => [...prev, message]);
      })();
    },
    [meetingId, userName, getPairwiseKey]
  );

  const endMeetingForAll = useCallback(() => {
    const socket = socketRef.current;
    if (!socket?.connected || !meetingId) return;
    socket.emit('meeting-ended', {
      meetingId,
      bySocketId: mySocketIdRef.current,
    });
  }, [meetingId]);

  const broadcastMediaState = useCallback(
    (state: ParticipantMediaState) => {
      const socket = socketRef.current;
      const socketId = mySocketIdRef.current;
      if (!socket?.connected || !meetingId || !socketId) return;
      socket.emit('media-state', { meetingId, socketId, ...state });
    },
    [meetingId]
  );

  const muteParticipant = useCallback(
    (targetSocketId: string) => {
      const socket = socketRef.current;
      if (!socket?.connected || !meetingId) return;
      if (hostSocketIdRef.current !== mySocketIdRef.current) return;
      socket.emit('force-mute', {
        meetingId,
        targetSocketId,
        fromHostSocketId: mySocketIdRef.current,
      });
    },
    [meetingId]
  );

  const sendReaction = useCallback(
    (emoji: string) => {
      const socket = socketRef.current;
      const socketId = mySocketIdRef.current;
      if (!socket?.connected || !meetingId || !socketId) return;

      socket.emit('meeting-reaction', { meetingId, socketId, emoji });
      applyReaction(socketId, emoji);
    },
    [applyReaction, meetingId]
  );

  const broadcastHandRaise = useCallback(
    (raised: boolean) => {
      const socket = socketRef.current;
      const socketId = mySocketIdRef.current;
      if (!socket?.connected || !meetingId || !socketId) return;

      socket.emit('hand-raise', { meetingId, socketId, raised });
      setParticipantSignals((prev) => ({
        ...prev,
        [socketId]: { ...prev[socketId], handRaised: raised },
      }));
    },
    [meetingId]
  );

  /** Host: bekleme odasındaki katılım isteğini onaylar veya reddeder. */
  const respondToJoinRequest = useCallback(
    (targetSocketId: string, approved: boolean) => {
      const socket = socketRef.current;
      if (!socket?.connected || !meetingId) return;
      socket.emit('join-response', {
        meetingId,
        targetSocketId,
        approved,
      });
    },
    [meetingId]
  );

  const kickParticipant = useCallback(
    (targetSocketId: string) => {
      const socket = socketRef.current;
      if (!socket?.connected || !meetingId) return;
      if (hostSocketIdRef.current !== mySocketIdRef.current) return;
      socket.emit('kick-participant', {
        meetingId,
        targetSocketId,
        fromHostSocketId: mySocketIdRef.current,
      });
    },
    [meetingId]
  );

  const replaceTrack = useCallback((newTrack: MediaStreamTrack) => {
    console.log('Replacing track:', newTrack.kind);
    
    peerConnectionsRef.current.forEach((pc, socketId) => {
      const sender = pc.getSenders().find(s => s.track?.kind === newTrack.kind);
      if (sender) {
        console.log('Replacing track for peer:', socketId);
        sender.replaceTrack(newTrack).catch(err => {
          console.error('Error replacing track for', socketId, err);
        });
        if (newTrack.kind === 'video') {
          void applyVideoSenderParameters(pc);
        }
      } else {
        console.log('No sender found for track kind:', newTrack.kind, 'peer:', socketId);
      }
    });
  }, [applyVideoSenderParameters]);

  const broadcastAgendaTimeline = useCallback(
    (sync: AgendaTimelineSync) => {
      const socket = socketRef.current;
      if (!socket?.connected || !meetingId) return;
      socket.emit('agenda-timeline', {
        meetingId,
        startedAt: sync.startedAt,
        currentIndex: sync.currentIndex,
        advancedAt: sync.advancedAt,
      });
    },
    [meetingId]
  );

  const broadcastMinutesPolicy = useCallback(
    (enabled: boolean) => {
      const socket = socketRef.current;
      if (!socket?.connected || !meetingId) return;
      socket.emit('minutes-policy', { meetingId, enabled });
    },
    [meetingId]
  );

  const isHost = !!mySocketId && mySocketId === hostSocketId;

  return {
    participants,
    isConnected,
    replaceTrack,
    collectNetworkMetrics,
    mySocketId,
    hostSocketId,
    isHost,
    chatMessages,
    sendChatMessage,
    chatEncryptionEnabled,
    mediaEncryptionEnabled,
    e2eRoomActive,
    e2eReadyPeers,
    participantMediaState,
    broadcastMediaState,
    muteParticipant,
    kickParticipant,
    endMeetingForAll,
    lobbyStatus,
    respondToJoinRequest,
    participantSignals,
    floatingReactions,
    sendReaction,
    broadcastHandRaise,
    broadcastAgendaTimeline,
    broadcastMinutesPolicy,
  };
};